import { useEffect, useState } from "react";
import { getUser, useEnvironment } from "../core/Global";
import { ensurePushSubscribed } from "../utilities/push";


function permissionNow(): NotificationPermission | undefined {
    if (!("Notification" in window)) return undefined
    return Notification.permission
}


// The browser permission behind every notification the app can raise. Shared by the
// banner that asks for it and the notification settings page that warns when it is
// missing, so both tell the user the same story.
export function useNotificationPermission() {

    const env = useEnvironment()

    const [permission, permissionSet] = useState<NotificationPermission | undefined>(permissionNow)
    const [working, workingSet] = useState<boolean>(false)
    // Chrome answers a permission request with its "quiet UI": no dialog, just a small
    // icon in the address bar, and the promise stays pending until that icon is used.
    // From the page it looks like nothing happened, so callers can say where to look.
    const [askedInBrowser, askedInBrowserSet] = useState<boolean>(false)

    // The permission can be changed from the browser's own UI at any moment, with no
    // event on the page. The Permissions API reports it where available, and coming
    // back to the tab covers the browsers that don't.
    useEffect(() => {

        const check = () => permissionSet(permissionNow())

        window.addEventListener("focus", check)
        document.addEventListener("visibilitychange", check)

        var status: PermissionStatus | undefined
        navigator.permissions?.query({ name: "notifications" as PermissionName })
            .then(res => {
                status = res
                res.onchange = check
            })
            .catch(() => undefined)

        return () => {
            window.removeEventListener("focus", check)
            document.removeEventListener("visibilitychange", check)
            if (status) status.onchange = null
        }

    }, [])

    const ask = () => {

        if (!("Notification" in window)) return

        workingSet(true)

        // Still unanswered after a moment? Then the browser did not put a dialog in
        // front of the user and they need to be pointed at the address bar.
        const quiet = setTimeout(() => askedInBrowserSet(true), 1500)

        Notification.requestPermission().then(result => {
            clearTimeout(quiet)
            permissionSet(result)
            workingSet(false)
            // "default" means the prompt was dismissed rather than answered.
            askedInBrowserSet(result != "granted")

            const user = getUser()
            if (result == "granted" && user?.id) {
                ensurePushSubscribed(env.hubUrl, user.id).catch(() => undefined)
            }
        }).catch(() => {
            clearTimeout(quiet)
            workingSet(false)
        })
    }

    return {
        permission,
        // No Notification API at all — nothing to ask for, only something to say.
        supported: permission != undefined,
        granted: permission == "granted",
        // Blocked can only be undone from the browser's own site settings: asking again
        // resolves straight back to "denied" without showing anything.
        blocked: permission == "denied",
        working,
        askedInBrowser,
        ask
    }
}
